import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react'

interface ShellState {
  /** How many PageHeaders are mounted. With none (a page not yet moved), the
   *  shell puts the attention strip at the top itself. */
  headers: number
  addHeader: () => () => void
  /** How many mounted pages asked for the full width (schedule, calendar):
   *  the shell drops its 1280px cap while any does. */
  wide: number
  addWide: () => () => void
}

const ShellContext = createContext<ShellState | null>(null)

export function ShellProvider({ children }: { children: ReactNode }) {
  const [headers, setHeaders] = useState(0)
  const [wide, setWide] = useState(0)
  const addHeader = useCallback(() => {
    setHeaders((n) => n + 1)
    return () => setHeaders((n) => n - 1)
  }, [])
  const addWide = useCallback(() => {
    setWide((n) => n + 1)
    return () => setWide((n) => n - 1)
  }, [])
  const value = useMemo(() => ({ headers, addHeader, wide, addWide }), [headers, addHeader, wide, addWide])
  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
}

export function useShell(): ShellState | null {
  return useContext(ShellContext)
}

/** Called by PageHeader: "this page draws its own header, put the strip under it". */
export function useRegisterHeader() {
  const shell = useShell()
  useLayoutEffect(() => shell?.addHeader(), [shell?.addHeader]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** Called by a page that needs the whole width — the schedule's timeline and
 *  the month grid read better without the content cap. */
export function useWidePage() {
  const shell = useShell()
  useLayoutEffect(() => shell?.addWide(), [shell?.addWide]) // eslint-disable-line react-hooks/exhaustive-deps
}
